import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  Divider,
  Button,
  Avatar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DevicesIcon from '@mui/icons-material/Devices';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import GroupIcon from '@mui/icons-material/Group';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import ThemeToggleButton from './ThemeToggleButton';
import LanguageSwitcher from './LanguageSwitcher';

const DRAWER_WIDTH = 272;

const NAV_KEYS: Array<{ path: string; tKey: string; icon: React.ReactElement }> = [
  { path: '/',              tKey: 'nav.dashboard',    icon: <DashboardIcon /> },
  { path: '/reports',       tKey: 'nav.reports',      icon: <AssessmentIcon /> },
  { path: '/devices',       tKey: 'nav.devices',      icon: <DevicesIcon /> },
  { path: '/write-offs',    tKey: 'nav.writeOffs',    icon: <DeleteSweepIcon /> },
  { path: '/assignments',   tKey: 'nav.assignments',  icon: <AssignmentReturnIcon /> },
  { path: '/organizations', tKey: 'nav.organizations', icon: <BusinessIcon /> },
  { path: '/employees',     tKey: 'nav.employees',    icon: <PeopleIcon /> },
  { path: '/settings',     tKey: 'nav.settings',     icon: <SettingsIcon /> },
];

const ADMIN_NAV_KEYS: Array<{ path: string; tKey: string; icon: React.ReactElement }> = [
  { path: '/users', tKey: 'nav.users', icon: <GroupIcon /> },
];

function initials(text: string): string {
  if (!text) return '?';
  const parts = text.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = theme.palette.mode === 'dark';
  const { t } = useTranslation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const closeMobile = () => {
    if (isMobile) setMobileOpen(false);
  };

  const allNavItems = [...NAV_KEYS, ...(isAdmin ? ADMIN_NAV_KEYS : [])];
  const activeLabel = allNavItems.find((i) => i.path === location.pathname)?.tKey
    ? t(allNavItems.find((i) => i.path === location.pathname)!.tKey)
    : '';

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '16px 14px' }}>
      {/* Brand */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 1.5,
          py: 1,
          mb: 1.5,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            background: isDark
              ? 'linear-gradient(135deg, #8b8bff, #5ee7f1)'
              : 'linear-gradient(135deg, #5b5bf6, #22c3d1)',
            boxShadow: isDark
              ? '0 8px 24px -8px rgba(139,139,255,0.8)'
              : '0 8px 24px -8px rgba(91,91,246,0.5)',
          }}
        />
        <Box sx={{ lineHeight: 1 }}>
          <Typography variant="subtitle1" fontWeight={800} sx={{ letterSpacing: '-0.02em' }}>
            {t('nav.brand')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'var(--text-subtle)' }}>
            {t('nav.tagline')}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 1 }} />

      {/* Main nav */}
      <List disablePadding>
        {NAV_KEYS.map((item) => (
          <ListItemButton
            key={item.path}
            selected={location.pathname === item.path}
            onClick={() => {
              navigate(item.path);
              closeMobile();
            }}
            sx={{ px: 1.5, py: 1 }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText
              primary={t(item.tKey)}
              primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
            />
          </ListItemButton>
        ))}
      </List>

      {/* Admin section */}
      {isAdmin && (
        <>
          <Typography
            variant="overline"
            sx={{
              px: 2,
              pt: 2,
              pb: 0.5,
              color: 'var(--text-subtle)',
              letterSpacing: '0.08em',
              fontSize: '0.68rem',
            }}
          >
            {t('nav.administration')}
          </Typography>
          <List disablePadding>
            {ADMIN_NAV_KEYS.map((item) => (
              <ListItemButton
                key={item.path}
                selected={location.pathname === item.path}
                onClick={() => {
                  navigate(item.path);
                  closeMobile();
                }}
                sx={{ px: 1.5, py: 1 }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={t(item.tKey)}
                  primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
                />
              </ListItemButton>
            ))}
          </List>
        </>
      )}

      {/* Spacer */}
      <Box sx={{ flexGrow: 1 }} />

      {/* User card + language + theme + logout */}
      <Box
        sx={{
          mt: 'auto',
          pt: 1.5,
        }}
      >
        {/* User card */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 1.5,
            py: 1.25,
            mb: 1.5,
            borderRadius: 1.5,
            border: '1px solid var(--border)',
            background: isDark
              ? 'linear-gradient(135deg, rgba(139,139,255,0.08), rgba(94,231,241,0.04))'
              : 'linear-gradient(135deg, rgba(91,91,246,0.08), rgba(34,195,209,0.04))',
          }}
        >
          <Avatar
            sx={{
              width: 40,
              height: 40,
              fontSize: '0.95rem',
              fontWeight: 700,
              background: isDark
                ? 'linear-gradient(135deg, #8b8bff, #5ee7f1)'
                : 'linear-gradient(135deg, #5b5bf6, #22c3d1)',
              color: isDark ? '#0a0b14' : '#ffffff',
              boxShadow: isDark
                ? '0 6px 20px -6px rgba(139,139,255,0.8)'
                : '0 6px 20px -6px rgba(91,91,246,0.5)',
            }}
          >
            {initials(user?.username || '')}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {user?.username}
            </Typography>
            <Typography variant="caption" sx={{ color: 'var(--text-muted)' }}>
              {user?.role === 'admin' ? t('nav.roleAdmin') : t('nav.roleOperator')}
            </Typography>
          </Box>
        </Box>

        {/* Language switcher */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
          <LanguageSwitcher />
        </Box>

        {/* Theme toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
          <ThemeToggleButton />
        </Box>

        <Button
          variant="outlined"
          size="small"
          fullWidth
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          sx={{ py: 1 }}
        >
          {t('nav.logout')}
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Fixed background mesh layer */}
      <Box className="app-bg" aria-hidden="true" />

      {/* Mobile AppBar */}
      {isMobile && (
        <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
          <Toolbar variant="dense" sx={{ gap: 1, minHeight: 56 }}>
            <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(!mobileOpen)} aria-label={t('nav.menu')}>
              <MenuIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: 1,
                  background: isDark
                    ? 'linear-gradient(135deg, #8b8bff, #5ee7f1)'
                    : 'linear-gradient(135deg, #5b5bf6, #22c3d1)',
                }}
              />
              <Typography variant="subtitle1" fontWeight={800} sx={{ letterSpacing: '-0.01em' }}>
                {t('nav.brandShort')}
              </Typography>
            </Box>
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <LanguageSwitcher />
              <ThemeToggleButton />
            </Box>
          </Toolbar>
        </AppBar>
      )}

      {/* Sidebar */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        ModalProps={isMobile ? { keepMounted: true } : undefined}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Main */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Desktop breadcrumb strip */}
        {!isMobile && (
          <Box
            sx={{
              px: { md: 4 },
              pt: 2.5,
              pb: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              color: 'var(--text-muted)',
            }}
          >
            <Typography variant="h5" fontWeight={700} sx={{ color: 'var(--text)', letterSpacing: '-0.01em' }}>
              {activeLabel}
            </Typography>
            <Box
              sx={{
                ml: 'auto',
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                '& > .pill': {
                  padding: '4px 10px',
                  fontSize: '0.72rem',
                  color: 'var(--text-muted)',
                  borderRadius: '999px',
                  border: '1px solid var(--border)',
                  background: 'var(--surface)',
                },
              }}
            >
              <span className="pill">{t('nav.online')}</span>
              <span className="pill">v3.0</span>
            </Box>
          </Box>
        )}

        <Box
          sx={{
            p: { xs: 2.5, md: 3.5 },
            pt: isMobile ? 9 : 2.5,
            flexGrow: 1,
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
